import { isValid } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import {
  AddressType,
  COMPRESSION_THRESHOLD_BYTES,
  DOCUMENT_COMPRESS_ENDPOINT_PATH,
  EnterpriseStructure,
  FINANCIAL_PRODUCT,
  VerificationType,
} from '@brighte/validation';
import { getConfig } from '@config';
import { datadogRum } from '@datadog/browser-rum';
import {
  AbrAbnLookupQuery,
  AbrAbnSearchQuery,
  AddJointApplicantMutation,
  AddJointApplicantMutationVariables,
  AddressDetailsQuery,
  AddressSuggestionsQuery,
  AddressSuggestionsResult,
  BankStatementIframe,
  CardPaymentMethod,
  DebitPaymentMethod,
  DocumentPresignedUrlQuery,
  DocumentPresignedUrlQueryVariables,
  ErrorResponse,
  FinancialProduct,
  FormValues,
  GetBankStatementIframeQuery,
  GetBankStatementIframeQueryVariables,
  InputMaybe,
  Me,
  MeQuery,
  MeQueryVariables,
  ModifiedApplicationQuote,
  PrePopValues,
  QuotePrepopValuesByTrackingIdQuery,
  QuotePrepopValuesByTrackingIdQueryVariables,
  UploadedDocumentsQuery,
  UploadedDocumentsQueryVariables,
  useAbrAbnLookupQuery,
  useAbrAbnSearchQuery,
  useAddJointApplicantMutation,
  useAddressDetailsQuery,
  useAddressSuggestionsQuery,
  useApplicationDetailsQuery,
  useCalculateRebatesQuery,
  useDocumentPresignedUrlQuery,
  useGetBankStatementIframeQuery,
  useMeQuery,
  useOpenBankingIncomeQuery,
  usePaymentDetailsByMethodIdQuery,
  useQuotePrepopValuesByTrackingIdQuery,
  useReferralQuery,
  useUploadedDocumentsQuery,
} from '@graphql/index';
import { determineApplicationSteps } from '@pages/ApplicationForm/hooks';
import { UseMutationOptions, UseQueryOptions, useMutation } from '@tanstack/react-query';
import { useApplicationData, useSetApplicationData, useStepperActions } from '../provider';
import { APPLICATION_STEPS, getApplicantTypeAndTrackingIdFromUrl, isJointApplicant, pascalToCamelCase } from '../utils';
import { client } from '../utils/client';
import { transformFieldValues } from './helper';

export { useLaunchDarklyContextUpdate } from './useLaunchDarklyContextUpdate';
export { useGetApplicationSubmissionStatus } from './useGetApplicationSubmissionStatus';
export { useTimer } from './useTimer';

export interface AddressSearch {
  formatted_address: string;
}

export interface GoogleAddress {
  unit_no?: string;
  street_no?: string;
  street_name?: string;
  suburb?: string;
  state?: string;
  country?: string;
  postcode?: string;
  lat?: number;
  lng?: number;
}

export interface BusinessSearch {
  abn: string;
  name: string;
}

// hooks that are used in components
export const useAddressSearch = (
  searchQuery: string,
  options?: UseQueryOptions<AddressSuggestionsQuery, string, string[]>,
) => {
  return useAddressSuggestionsQuery(
    { input: { query: searchQuery } },
    {
      // * Start fetching when input value has at least 3 characters
      enabled: !!searchQuery && searchQuery.trim().length >= 3,
      select: data => {
        return (data.addressSuggestions as AddressSuggestionsResult[]).map(
          address => address.formattedAddress as string,
        );
      },
      useErrorBoundary: true,
      ...options,
    },
  );
};

export const useAddressDetails = (
  formattedAddress: InputMaybe<string>,
  options?: UseQueryOptions<AddressDetailsQuery, string, AddressType>,
) => {
  return useAddressDetailsQuery(
    { input: { query: formattedAddress ?? '' } },
    {
      enabled: !!formattedAddress,
      select: (data): AddressType => data.addresses as AddressType,
      onError: error => error,
      ...options,
    },
  );
};

export function useDebounce<T = string>(value: T, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

//Added query for ABN lookup
export function useBusinessSearch(
  query: InputMaybe<string>,
  options?: UseQueryOptions<AbrAbnSearchQuery, string, Array<BusinessSearch>>,
) {
  return useAbrAbnSearchQuery(
    {
      input: { query: query?.replace(/\s+/g, '') ?? '' },
    },
    {
      // * Start fetching when input value is 6 characters or more
      enabled: Number(query?.length) >= 6,
      select: data => {
        return data.abrAbnSearch as Array<BusinessSearch>;
      },
      useErrorBoundary: false,
      retry: 0,
      ...options,
    },
  );
}

export function useAbnLookup(
  abn: InputMaybe<string>,
  options?: UseQueryOptions<AbrAbnLookupQuery, string, AbrAbnLookupQuery['abrAbnLookup']>,
) {
  const normalised = abn?.replace(/\s+/g, '') ?? '';
  return useAbrAbnLookupQuery(
    { input: { abn: normalised } },
    {
      enabled: normalised.length === 11,
      select: data => data.abrAbnLookup,
      useErrorBoundary: false,
      retry: 0,
      ...options,
    },
  );
}

export function useQuotePrepopValuesByTrackingId(
  variables: QuotePrepopValuesByTrackingIdQueryVariables,
  options?: UseQueryOptions<QuotePrepopValuesByTrackingIdQuery, ErrorResponse, PrePopValues>,
) {
  return useQuotePrepopValuesByTrackingIdQuery(variables, {
    select: data => data.quotePrepopValuesByTrackingId,
    ...options,
  });
}

export function useAddJointApplicant(
  options?: UseMutationOptions<AddJointApplicantMutation, unknown, AddJointApplicantMutationVariables>,
) {
  return useAddJointApplicantMutation(options);
}
export function usePrepop() {
  const [product, setProduct] = useState<Partial<FinancialProduct>>();
  const applicationQuote = useApplicationData('quote');
  const setApplicationData = useSetApplicationData();

  const { getCurrentSession, setCurrentSession } = useAuth0();
  const sessionSetRef = useRef(false);

  const otp = getCurrentSession()?.otp || '';

  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const isJoint = isJointApplicant();

  const { isLoading, data, error } = useQuotePrepopValuesByTrackingId(
    {
      trackingId,
      joint: isJoint,
    },
    {
      onSuccess: data => {
        // When arriving from customer-connect, the AuthProvider doesn't know the phone
        // number yet so it can't find cached credentials. Setting the current session
        // with the phone from prepop lets the AuthProvider re-initialise with the
        // correct cache key and pick up the existing auth tokens from localStorage.
        if (data.phone && !sessionSetRef.current) {
          sessionSetRef.current = true;
          setCurrentSession(data.phone);
        }

        const quote = {
          ...applicationQuote,
          financialProduct: data.financialProduct,
          status: data.status,
        } as ModifiedApplicationQuote;

        const newApplicationData = {
          ContactDetails: {
            email: data.email,
            mobile: data.phone,
            maskedMobile: data?.maskedPhone,
            maskedEmail: data?.maskedEmail,
            verificationType: data.verificationType ?? VerificationType.Sms,
            otp,
          },
          quote,
        };

        setProduct(data.financialProduct);
        setApplicationData(newApplicationData as Partial<FormValues>);
      },
      useErrorBoundary: true,
      retry: 0,
    },
  );

  return { isLoading, product, trackingId, data, error };
}

export function useGetBankStatementIframe(
  variables: GetBankStatementIframeQueryVariables,
  options?: UseQueryOptions<GetBankStatementIframeQuery, string, BankStatementIframe>,
) {
  return useGetBankStatementIframeQuery(variables, {
    select: data => data.getBankStatementIframe,
    ...options,
  });
}

export function useQuoteByTrackingId({
  enabled = true,
  enableApplication = true,
}: {
  enabled: boolean;
  enableApplication?: boolean;
}) {
  const setApplicationData = useSetApplicationData();
  const { onNext } = useStepperActions();
  const [isLoading, setIsLoading] = useState(true);
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const joint = isJointApplicant();
  const { user } = useAuth0();

  //query for quote and application details
  const { data: quote, isLoading: isQuoteLoading } = useReferralQuery(
    { id: trackingId, joint },
    {
      enabled,
      select: data => {
        const { repaymentOptions, relatedReferrals, selectedPackage, ...referral } = data.referral;

        datadogRum.setUserProperty('vendor', referral?.vendor.tradingName);

        const selectedIds = selectedPackage?.trackingIds ?? [];

        const filteredRelatedReferrals =
          selectedIds && selectedIds.length > 0
            ? relatedReferrals?.filter(relatedReferral => selectedIds.includes(relatedReferral?.id ?? ''))
            : undefined;

        return {
          ...referral,
          ...(repaymentOptions && {
            repaymentOptions: repaymentOptions,
          }),
          ...(filteredRelatedReferrals && {
            relatedReferrals: filteredRelatedReferrals.map(relatedReferral => ({
              ...relatedReferral,
              ...(relatedReferral.repaymentOptions && {
                //copying expiry and financialProduct details to related quote as it will be same as parent quote
                repaymentOptions: relatedReferral.repaymentOptions,
                expiryDate: referral.expiryDate,
                financialProduct: referral.financialProduct,
                productConfiguration: referral.productConfiguration,
              }),
            })),
          }),
        };
      },
      useErrorBoundary: true,
    },
  );

  const { data: application, isLoading: isApplicationLoading } = useApplicationDetailsQuery(
    { applicationId: trackingId, joint },
    {
      enabled: enableApplication && enabled,
      select: data => data.application,
      retry: 0,
    },
  );

  const { data: paymentDetails, isLoading: isPaymentDetailsLoading } = usePaymentDetailsByMethodIdQuery(
    { paymentMethodId: application?.payment?.methodId ?? '', userId: user ? user.id : '' },
    {
      select: data => data.paymentMethod,
      enabled: !!application?.payment?.methodId && enableApplication && !joint,
      retry: 0,
    },
  );

  // open banking data will only be fetched if applicant is using open banking
  const applicant = joint ? application?.jointApplicant : application?.primaryApplicant;
  const enableOpenBanking = Boolean(trackingId && applicant && applicant?.usingOpenBanking);
  const { data: openBankingIncomeData, isLoading: isOpenBankingDataLoading } = useOpenBankingIncomeQuery(
    { applicationId: trackingId },
    { enabled: enableOpenBanking, retry: 0, select: data => data?.openBankingIncome },
  );
  const openBankingMonthlyIncome = openBankingIncomeData?.totalMonthlyIncome;
  const isOpenBankingDataFetching = enableOpenBanking ? isOpenBankingDataLoading : false;

  //payment details will only be fetched if methodId is present
  const isPaymentDetailsFetching =
    enableApplication && application?.payment?.methodId && !joint ? isPaymentDetailsLoading : false;

  const isDataFetchComplete =
    isLoading && !isQuoteLoading && !isApplicationLoading && !isPaymentDetailsFetching && !isOpenBankingDataFetching;

  useEffect(() => {
    if (isDataFetchComplete) {
      if (quote && application) {
        const customerType = quote.financialProduct.customerType;
        const isOpenBankingConsentComplete = applicant?.usingOpenBanking ?? false;

        const structure = application.enterprise?.structure as keyof typeof EnterpriseStructure;
        const structureProp = structure ? pascalToCamelCase(structure) : undefined;
        const entityStructure = EnterpriseStructure[structure];
        const ownershipType = structureProp ? application.enterprise?.[structureProp]?.ownershipType : undefined;

        const steps = determineApplicationSteps(
          customerType,
          application?.payment?.isCashApplication ?? false,
          isOpenBankingConsentComplete,
          entityStructure,
          ownershipType,
        );

        let payment = {};

        if (paymentDetails) {
          if (paymentDetails.type === 'CARD') {
            const cardDetails = paymentDetails as CardPaymentMethod;
            payment = {
              type: 'CARD',
              creditCard: {
                cardHolder: cardDetails.cardHolder,
                cardNumber: cardDetails.maskedCardNumber,
                cardExpiry: cardDetails.cardExpiry,
                token: cardDetails.token,
                cardType: cardDetails.cardType,
              },
            };
          } else {
            const cardDetails = paymentDetails as DebitPaymentMethod;
            payment = {
              type: 'DEBIT',
              directDebit: {
                accountName: cardDetails.accountName,
                accountNumber: `xxxx${cardDetails.accountLast4}`,
                bsb: cardDetails.accountBsb,
              },
            };
          }
        }

        const { formValues, currentStepIndex } = transformFieldValues(
          steps,
          quote as ModifiedApplicationQuote,
          application,
          payment,
        );
        setIsLoading(false);
        setApplicationData({ ...formValues, applicationId: trackingId, application });

        const isSummaryStep = steps[currentStepIndex] === APPLICATION_STEPS.SUMMARY;
        const incomeReviewStepIndex = steps.findIndex(step => step === APPLICATION_STEPS.INCOME_REVIEW);

        const shouldRedirectToIncomeReview =
          isOpenBankingConsentComplete && !openBankingMonthlyIncome && isSummaryStep && incomeReviewStepIndex !== -1;

        if (shouldRedirectToIncomeReview) {
          onNext(incomeReviewStepIndex);
        } else {
          onNext(currentStepIndex);
        }
      } else {
        setIsLoading(false);
        setApplicationData({ quote: quote as ModifiedApplicationQuote });
      }
    }
  }, [isDataFetchComplete, onNext, joint, openBankingMonthlyIncome]);

  return {
    isLoading,
  };
}

export function useDatadogAddError() {
  const { trackingId } = getApplicantTypeAndTrackingIdFromUrl();
  const isJoint = isJointApplicant();
  const { data } = useQuotePrepopValuesByTrackingId({ trackingId, joint: isJoint }, { useErrorBoundary: false });

  const logError = useCallback((error: Error, context: object = {}) => {
    const errorMessage = typeof error === 'string' ? error : error?.message;
    if (errorMessage) {
      const logMessage = `${data?.financialProduct.id ?? ''} error: ${errorMessage}`;
      datadogRum.addError(new Error(logMessage), error);
      datadogRum.addAction('error', {
        message: logMessage,
        trackingId,
        jointApplicant: isJoint,
        ...context,
      });
    }
  }, []);

  return {
    logError,
  };
}

export function useScrollToTop() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
}

export function useUploadFilesToS3() {
  return useMutation(['uploadFile'], (input: { bucketUrl: string; file: FormData }) => {
    return client(input.bucketUrl, {
      data: input.file,
      config: { credentials: 'omit', mode: 'cors', isTokenRequired: false },
    });
  });
}

// Reusable pre-upload document compression. Large files are sent to the
// backend compress endpoint, which returns a smaller file; the caller then runs
// its normal (presigned) upload flow with the returned file. Small files skip the
// round-trip, and any failure falls back to the original so uploads are never blocked.

export function useResizeDocumentForUpload() {
  const { getAccessTokenSilently } = useAuth0();

  return useCallback(
    async (file: File): Promise<File> => {
      if (file.size < COMPRESSION_THRESHOLD_BYTES) {
        return file;
      }

      try {
        const credentials = await getAccessTokenSilently();
        const token = credentials && 'accessToken' in credentials ? credentials.accessToken : undefined;
        if (!token) {
          return file;
        }

        const formData = new FormData();
        formData.append('file', file);

        const endpoint = `${new URL(getConfig().url).origin}${DOCUMENT_COMPRESS_ENDPOINT_PATH}`;
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          return file;
        }

        const compressed = await response.blob();
        // Keep whichever is smaller; preserve the original filename for backend classification.
        if (compressed.size >= file.size) {
          return file;
        }

        return new File([compressed], file.name, { type: compressed.type || file.type });
      } catch {
        return file;
      }
    },
    [getAccessTokenSilently],
  );
}

export function useApplicationDocumentUrl(
  variables: DocumentPresignedUrlQueryVariables,
  options?: UseQueryOptions<DocumentPresignedUrlQuery, string, string>,
) {
  return useDocumentPresignedUrlQuery(variables, {
    enabled: !!variables.applicationDocumentGetPresignedUrlInput.documentId,
    select: data => data.applicationDocumentPresignedUrl,
    ...options,
  });
}

export function useUploadedDocuments(
  variables: UploadedDocumentsQueryVariables,
  options?: UseQueryOptions<UploadedDocumentsQuery, string, Array<string> | undefined>,
) {
  return useUploadedDocumentsQuery(variables, {
    enabled: !!variables.applicationId,
    select: data => data?.application?.documents.map(doc => doc.id),
    ...options,
  });
}

export const useUserDetails = (variables?: MeQueryVariables, options?: UseQueryOptions<MeQuery, string, Me>) => {
  return useMeQuery(variables, {
    select: data =>
      ({
        ...data.me,
        middleName: data.me.middleName ?? '',
        dateOfBirth: isValid(data.me.dateOfBirth) ? new Date(data.me.dateOfBirth) : '',
        fpSynced: isValid(data.me.fpSynced) ? new Date(data.me.fpSynced) : null,
      }) as Me,
    ...options,
  });
};

export const useRebateAmount = ({
  financialProductId,
  quote,
}: {
  financialProductId?: string;
  quote?: ModifiedApplicationQuote;
}) => {
  const { user, verificationCompleted } = useAuth0();
  const purchaseItems = [quote, ...(quote?.relatedReferrals ?? [])].filter(Boolean).map(quote => ({
    categorySlug: quote?.category?.slug ?? '',
    purchaseTotal: quote?.totalPurchaseAmount ?? 0,
    reference: quote?.referenceNumber ?? '',
  }));

  const { data } = useCalculateRebatesQuery(
    {
      input: {
        financialProductSlug: financialProductId || '',
        purchaseItems,
        yearlyHouseholdGrossIncome: quote?.opportunity?.householdIncome,
      },
    },
    {
      enabled:
        !!user &&
        verificationCompleted &&
        !!quote &&
        !!financialProductId &&
        financialProductId === FINANCIAL_PRODUCT.ELECTRIFY_2515,
    },
  );

  return data?.calculateRebates?.totalRebateAmount || 0;
};
